import { LucideIcon } from 'components/LucideIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { Separator } from 'designSystem/components/Separator'
import { useSidebar } from 'designSystem/components/Sidebar'
import { Text } from 'designSystem/components/Text'
import React, { useCallback, useMemo, useState } from 'react'
import { GeckoSite } from 'types'

import { EmbeddedSelectorSection } from './EmbeddedSelectorSection'
import {
  EmbeddedSelectorChevron,
  EmbeddedSelectorIcon,
  EmbeddedSelectorLabels,
  EmbeddedSelectorTriggerButton,
} from './siteSelectorsSidebar.styles'
import { OrganizationWithSites, SiteSelectorsProps } from './siteSelectorsTypes'
import { useEmbeddedSelectorActions } from './useEmbeddedSelectorActions'
import { useSiteSelectorsState } from './useSiteSelectorsState'

const EmbeddedSiteSelectorTrigger: React.FC<{
  organizationLabel: string
  siteLabel: string
  disabled: boolean
}> = ({ organizationLabel, siteLabel, disabled }) => {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  return (
    <PopoverTrigger asChild>
      <EmbeddedSelectorTriggerButton
        type='button'
        $collapsed={isCollapsed}
        disabled={disabled}
        data-testid='org-dropdown'>
        <EmbeddedSelectorIcon>
          <LucideIcon name='Globe' size={18} />
        </EmbeddedSelectorIcon>
        <EmbeddedSelectorLabels $collapsed={isCollapsed}>
          <Text as='span' variant='caption' weight='semibold' truncate>
            {organizationLabel}
          </Text>
          <Text as='span' variant='caption' color='muted' truncate>
            {siteLabel}
          </Text>
        </EmbeddedSelectorLabels>
        <EmbeddedSelectorChevron $collapsed={isCollapsed}>
          <LucideIcon name='ChevronsUpDown' size={18} />
        </EmbeddedSelectorChevron>
      </EmbeddedSelectorTriggerButton>
    </PopoverTrigger>
  )
}

export const SiteSelectorsSidebar: React.FC<SiteSelectorsProps> = ({
  organizations,
  selectedOrganization,
  sites,
  selectedSite,
}) => {
  const {
    orgsWithAssociatedSites,
    sitesFilteredByOrganization,
    navigate,
    setDrawerEntry,
    setDrawerState,
    setOrgAndSiteIds,
    handleSiteSelect,
    handleEmbeddedOrgSelect,
    canEditOrganizations,
    canEditSites,
  } = useSiteSelectorsState({
    organizations,
    selectedOrganization,
    selectedSite,
    sites,
  })

  const [embeddedOpenList, setEmbeddedOpenList] = useState<
    'organization' | 'site' | null
  >(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [embeddedOrgSearch, setEmbeddedOrgSearch] = useState('')
  const [embeddedSiteSearch, setEmbeddedSiteSearch] = useState('')

  const resetEmbeddedSelectors = useCallback(() => {
    setEmbeddedOpenList(null)
    setEmbeddedOrgSearch('')
    setEmbeddedSiteSearch('')
  }, [])

  const {
    handleAddOrganization,
    handleAddSite,
    handleEditOrganization,
    handleEditSite,
  } = useEmbeddedSelectorActions({
    resetEmbeddedSelectors,
    setIsPopoverOpen,
    selectedOrganizationId: selectedOrganization?.id,
    navigate,
    setOrgAndSiteIds,
    setDrawerEntry,
    setDrawerState,
  })

  const filteredEmbeddedOrganizations = useMemo(() => {
    const searchTerm = embeddedOrgSearch.trim().toLowerCase()
    if (!searchTerm) return orgsWithAssociatedSites
    return orgsWithAssociatedSites.filter(({ organization }) =>
      organization.data.name.toLowerCase().includes(searchTerm)
    )
  }, [embeddedOrgSearch, orgsWithAssociatedSites])

  const filteredEmbeddedSites = useMemo(() => {
    const searchTerm = embeddedSiteSearch.trim().toLowerCase()
    if (!searchTerm) return sitesFilteredByOrganization
    return sitesFilteredByOrganization.filter((site) =>
      site.data.name.toLowerCase().includes(searchTerm)
    )
  }, [embeddedSiteSearch, sitesFilteredByOrganization])

  const organizationLabel = selectedOrganization?.data.name ?? 'Organization'
  const siteLabel = selectedSite?.data.name ?? 'Work Site'
  const canOpenSelector =
    orgsWithAssociatedSites.length > 1 ||
    sitesFilteredByOrganization.length > 1 ||
    canEditOrganizations ||
    canEditSites

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open)
        if (!open) resetEmbeddedSelectors()
      }}>
      <EmbeddedSiteSelectorTrigger
        organizationLabel={organizationLabel}
        siteLabel={siteLabel}
        disabled={!canOpenSelector}
      />
      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={4}
        style={{ width: '15rem', gap: 0, padding: 0, overflow: 'hidden' }}>
        <EmbeddedSelectorSection<OrganizationWithSites>
          label='Organization'
          selectedLabel={organizationLabel}
          selectedEditLabel={selectedOrganization?.data.name}
          onEditSelected={
            selectedOrganization
              ? (event) =>
                  void handleEditOrganization(event, selectedOrganization)
              : undefined
          }
          items={filteredEmbeddedOrganizations}
          getItemId={(item) => item.organization.id}
          getItemName={(item) => item.organization.data.name}
          isOpen={embeddedOpenList === 'organization'}
          onToggleOpen={() =>
            setEmbeddedOpenList((openList) =>
              openList === 'organization' ? null : 'organization'
            )
          }
          searchValue={embeddedOrgSearch}
          searchPlaceholder='Search organizations...'
          onSearchChange={setEmbeddedOrgSearch}
          canEdit={canEditOrganizations}
          canAdd={canEditOrganizations}
          addLabel='Add Org'
          onAdd={handleAddOrganization}
          onEdit={(event, item) =>
            void handleEditOrganization(event, item.organization)
          }
          onSelect={(item) => {
            setEmbeddedOpenList(null)
            setEmbeddedOrgSearch('')
            void handleEmbeddedOrgSelect(item)
          }}
        />
        <Separator />
        <EmbeddedSelectorSection<GeckoSite>
          label='Site'
          selectedLabel={siteLabel}
          selectedEditLabel={selectedSite?.data.name}
          onEditSelected={
            selectedSite
              ? (event) => void handleEditSite(event, selectedSite)
              : undefined
          }
          items={filteredEmbeddedSites}
          getItemId={(site) => site.id}
          getItemName={(site) => site.data.name}
          isOpen={embeddedOpenList === 'site'}
          onToggleOpen={() =>
            setEmbeddedOpenList((openList) =>
              openList === 'site' ? null : 'site'
            )
          }
          searchValue={embeddedSiteSearch}
          searchPlaceholder='Search sites...'
          onSearchChange={setEmbeddedSiteSearch}
          canEdit={canEditSites}
          canAdd={Boolean(canEditSites && selectedOrganization?.id)}
          addLabel='Add Site'
          triggerDisabled={sitesFilteredByOrganization.length === 0}
          onAdd={handleAddSite}
          onEdit={(event, site) => void handleEditSite(event, site)}
          onSelect={(site) => {
            setEmbeddedOpenList(null)
            setEmbeddedSiteSearch('')
            void handleSiteSelect(site)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
